import { useEffect, useRef, useState } from 'react';
import { VehiculeCheckFilterType } from '@/entities/vehicle';
import { useLocalStorageState } from '../local-storage.hooks';
import debounce from 'lodash/debounce';
import { Dayjs } from 'dayjs';
import { VehicleCheckLocalStorage } from '@/entities/vehicle-check';

interface VehicleCheckFilterProps {
  setCurrentPage: (page: number) => void;
}

export const useVehicleCheckFilter = ({
  setCurrentPage,
}: VehicleCheckFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] =
    useState<VehiculeCheckFilterType>({ key: '' });
  const filtersEmptyRef = useRef<boolean>(true);

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: VehicleCheckLocalStorage.KEY,
    removeOnClose: true,
  });

  const [type, setType] = useLocalStorageState({
    key: VehicleCheckLocalStorage.TYPE,
    removeOnClose: true,
  });

  const [status, setStatus] = useLocalStorageState({
    key: VehicleCheckLocalStorage.STATUS,
    removeOnClose: true,
  });

  const [startDate, setStartDate] = useLocalStorageState({
    key: VehicleCheckLocalStorage.START_DATE,
    removeOnClose: true,
  });

  const [endDate, setEndDate] = useLocalStorageState({
    key: VehicleCheckLocalStorage.END_DATE,
    removeOnClose: true,
  });

  const [groupId, setGroupId] = useLocalStorageState({
    key: VehicleCheckLocalStorage.GROUP_ID,
    removeOnClose: true,
  });

  const [companyId, setCompanyId] = useLocalStorageState({
    key: VehicleCheckLocalStorage.COMPANY_ID,
    removeOnClose: true,
  });

  const [branchId, setBranchId] = useLocalStorageState({
    key: VehicleCheckLocalStorage.BRANCH_ID,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      key: key || '',
      type,
      status,
      startDate,
      endDate,
      groupId,
      companyId,
      branchId,
    }));

    filtersEmptyRef.current =
      !key &&
      !type &&
      !status &&
      !startDate &&
      !endDate &&
      !groupId &&
      !companyId &&
      !branchId;
  }, [key, type, status, startDate, endDate, groupId, companyId, branchId]);

  const debounceFilterUpdate = (value: string) => {
    setKey(value);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchVehiculeCheckType = (value: string) => {
    setType(value);
    setDebouncedFilter((prev) => ({ ...prev, type: value }));
  };

  const searchByStatus = (value: string) => {
    setStatus(value);
    setDebouncedFilter((prev) => ({ ...prev, status: value }));
  };

  const searchBeginDate = (value: Dayjs) => {
    setStartDate(value?.toISOString());
    setDebouncedFilter((prev) => ({
      ...prev,
      startDate: value?.toISOString(),
    }));
  };

  const searchEndDate = (value: Dayjs) => {
    setEndDate(value?.toISOString());
    setDebouncedFilter((prev) => ({ ...prev, endDate: value?.toISOString() }));
  };

  const searchByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, groupId: e.target.value }));
  };

  const searchByCompany = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, companyId: e.target.value }));
  };

  const searchByBranch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBranchId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, branchId: e.target.value }));
  };

  const clearFilters = () => {
    setKey('');
    setType('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setGroupId('');
    setCompanyId('');
    setBranchId('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    type,
    status,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    debounceFilterUpdate,
    searchVehiculeCheckType,
    searchByStatus,
    searchBeginDate,
    searchEndDate,
    searchByGroup,
    searchByCompany,
    searchByBranch,
  };
};
